import React, { useEffect, useState } from 'react'
import styles from './A23GameTableLoader.module.css';
import gameAssets from '@themed-assets';
import useGameConnection from '@/src/hooks/data/useGameConnection';
import Lottie from 'lottie-react';
import { GAME_SOCKET_CONNECTED, GAME_SOCKET_REMOVED, MATCH_MAKING_BET_DISABLED, CHECK_GAME_API_READY_TO_REQUEST, CHECK_GAME_API_REQUEST_INITIATED, CHECK_GAME_API_RESPONSE_SUCCESS } from '@/Poker/data/Constants';
import { useDispatch, useSelector } from 'react-redux';
import { 
  useGameEnded,
  useTourneyGameCompleted
} from '@/src/hooks/data/useGameTable';
import { deleteGameTable } from '@/Poker/redux/slices/gameTableSlice';

export default function A23GameTableLoader({ tempTableId }) {
  const tourneyId = useSelector((state) => state.gameTable.games[tempTableId].tourneyId);
  const { gameConnectionStatus, connected } = useGameConnection(tempTableId);
  const gameEnded = useGameEnded(tempTableId);
  const tourneyGameCompleted = useTourneyGameCompleted(tempTableId);
  const [randomPokerFact, setRandomPokerFact] = useState([])
  const dispatch = useDispatch();
  const maintenanceMode = gameConnectionStatus === GAME_SOCKET_REMOVED || gameConnectionStatus === MATCH_MAKING_BET_DISABLED;


  const handleExitButton = () => {
    dispatch(deleteGameTable({ tempTableId }));
  }

  const returnConnectionBasedText = () => {
    switch (gameConnectionStatus) {
      case MATCH_MAKING_BET_DISABLED:
      case GAME_SOCKET_REMOVED:
        return 'We are currently performing maintenance. Please exit the table.';
      case CHECK_GAME_API_READY_TO_REQUEST:
      case CHECK_GAME_API_REQUEST_INITIATED:
        return 'There was an issue with your connection. We\'re trying to reconnect...';
      case CHECK_GAME_API_RESPONSE_SUCCESS:
      case GAME_SOCKET_CONNECTED:
        return 'Almost there...we\'re getting you back in the game...';
      default:
        return tourneyId ? 'Your tournament is about to begin...' : 'Hold your cards, we\'re finding a table...';
    }
  }

  const returnRandomPokerFact = () => {
    const pokerFacts = [
      "The first recorded use of the word 'poker' was in 1655 in a book called 'The Game of the Dogs'",
      "The first modern poker tournament was held in 1970 at the Horseshoe Casino in Las Vegas",
      "The highest possible hand in poker is a Royal Flush, which consists of an Ace, King, Queen, Jack, and Ten of the same suit",
      "The term 'poker face' was first used in the 1920s",
      "The World Series of Poker is the most prestigious poker tournament in the world",
      "The most common poker hand is a pair of Jacks",
      "The most valuable hand in poker is a Royal Flush",
      "Poker is believed to have originated in the early 19th century in the United States.",
      "The longest poker game in history reportedly lasted 8 years, 5 months, and 3 days.",
      "The four suits in a deck of cards represent the four major pillars of the economy in the Middle Ages: hearts (church), spades (military), diamonds (merchants), and clubs (agriculture).",
      "The odds of getting a Royal Flush are 1 in 649,740.",
      "The term 'nuts' in poker refers to the best possible hand at a given moment.",
      "The phrase 'ace in the hole' comes from poker and means having a hidden advantage.",
      "Poker is played in more than 100 countries worldwide.",
      "The largest ever online poker pot was over $1.3 million.",
      "The first televised poker tournament was the World Series of Poker in 1973.",
      "The 'dead man's hand' is a pair of aces and eights, supposedly held by Wild Bill Hickok when he was shot.",
      "The Joker card was introduced to American card decks around 1860 for the game of Euchre, not poker.",
      "The most popular poker variant is Texas Hold'em.",
      "The 'river' is the final community card dealt in Texas Hold'em and Omaha.",
      "The 'flop' refers to the first three community cards dealt face up.",
      "The 'turn' is the fourth community card dealt in Hold'em and Omaha.",
      "The term 'fish' refers to an inexperienced or unskilled poker player.",
      "The term 'shark' refers to a highly skilled poker player.",
      "The largest live poker tournament prize ever won was $18.3 million.",
      "The World Series of Poker Main Event bracelet is considered the most prestigious prize in poker.",
      "The 'blind' is a forced bet posted by players to the left of the dealer button.",
      "The 'button' rotates clockwise after each hand to ensure fairness.",
      "The 'muck' is the pile of folded and burned cards.",
      "The 'kicker' is a side card used to break ties between hands of the same rank.",
      "The 'bubble' is the last spot before players make the money in a tournament.",
      "The 'chip leader' is the player with the most chips in a tournament.",
      "The 'short stack' is the player with the fewest chips at the table.",
      "The 'big blind' is usually twice the size of the 'small blind.'",
      "The 'ante' is a small forced bet contributed by all players before a hand begins.",
      "The 'check-raise' is a deceptive play where a player checks, then raises after an opponent bets.",
      "The 'all-in' move means betting all of your remaining chips.",
      "The 'bad beat' is when a strong hand loses to an unlikely draw.",
      "The 'slow roll' is when a player takes a long time to reveal a winning hand, considered poor etiquette.",
      "The 'tilt' is a state of emotional frustration that leads to poor decision-making.",
      "The 'freeroll' is a tournament with no entry fee but real prizes.",
      "The 'sit and go' is a tournament that starts as soon as all seats are filled.",
      "The 'satellite' is a smaller tournament where the prize is entry to a larger event.",
      "The 'straddle' is an optional blind bet made before cards are dealt.",
      "The 'gutshot' is a straight draw needing one specific card to complete.",
      "The 'open-ended straight draw' can be completed by two different cards.",
      "The 'full house' is a hand with three of a kind and a pair.",
      "The 'quads' is poker slang for four of a kind.",
      "The 'wheel' is the lowest possible straight: A-2-3-4-5.",
      "The 'bounty' is a reward for eliminating a player in certain tournaments.",
      "The 'rebuy' allows players to buy more chips after losing their stack in some tournaments.",
      "The 're-raise' is raising after someone else has already raised.",
      "The 'pot odds' help players decide whether to call a bet based on the size of the pot and the bet.",
      "The 'implied odds' consider future bets that can be won if a draw is completed.",
      "The 'value bet' is a bet made to extract the most value from a strong hand.",
      "The 'bluff' is betting or raising with a weak hand to make opponents fold better hands.",
      "The 'semi-bluff' is betting with a drawing hand that could improve to the best hand."
    ]

    return pokerFacts[Math.floor(Math.random() * pokerFacts.length)];
  }

  useEffect(() => {
    setRandomPokerFact(returnRandomPokerFact());
  }, [connected, maintenanceMode])

  if (((connected && !maintenanceMode) || gameEnded || tourneyGameCompleted) && !maintenanceMode) {
    return null;
  }

  return (
    <div className={styles.loaderContainer}>
      <div className={styles.loaderContent}>
        <img className={styles.loaderLogo} src={gameAssets.verticalLogo} alt="A23 Vertical Logo" />
        <div className={styles.loaderTopSection}>
          <div className={styles.loaderLottieContainer}>
            <Lottie style={{ width: '80px' }} animationData={gameAssets.pokerChips} loop={true} />
          </div>
          <div className={styles.loaderTextSection}>
            <span>{returnConnectionBasedText()}</span>
          </div>
          {
            gameConnectionStatus === GAME_SOCKET_REMOVED ?
              <div onClick={handleExitButton} className={styles.exitButton}>
                Exit
              </div>
              : null
          }
          <div className={styles.divider}></div>
          <div className={styles.didYouKnowSection}>
            <div className={styles.didYouKnowLeft}>
              <img className={styles.didYouKnowIcon} src={gameAssets.didYouKnow} alt="A23 Bulb" />
            </div>
            <div className={styles.didYouKnowRight}>
              <span className={styles.didYouKnowContent}>{randomPokerFact}</span>
            </div>
          </div>
        </div>

        {/* {
          import.meta.env.VITE_REACT_APP_ENVIRONMENT !== 'production' ?
            <div className={styles.gameConnectionStatus}>
              <span>{gameConnectionStatus}</span>
            </div>
            : null
        } */}
      </div>
    </div>
  )
}
